import { useCallback } from 'react';

import { Platform } from 'react-native';

import { getBaseOs } from 'react-native-device-info';
import { useDispatch, useSelector } from 'react-redux';

import { NavigationProp } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { AxiosError } from 'axios';

import { LANGUAGE_KEYS } from 'config/languageKeys.ts';

import { SCREEN } from 'router/screens.ts';
import { RouteNavigationParams } from 'router/types.ts';

import {
    getConsentOrdering,
    getLanguages,
    setSplashScreenState,
} from 'store/config/actions.ts';
import { selectAppConfig } from 'store/config/selectors.ts';
import { getPinsStatus, loginSilently } from 'store/user/actions.ts';

import { RegularStorage, StorageKeys } from 'services/mmkv';
import { RetryController } from 'services/opf-retry-mechanism';
import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import {
    isMobileMediaQuery,
    isTabletMediaQuery,
    isWebMinWidthMediaQuery,
} from 'utils/responsiveness.ts';

function useSilentLogin(
    navigation:
        | StackNavigationProp<RouteNavigationParams, SCREEN.ROOT>
        | NavigationProp<RouteNavigationParams>,
    onOpenInitialPage: () => Promise<void>,
) {
    const dispatch = useDispatch();

    const { languages } = useSelector(selectAppConfig);

    const handleSilentLoginSuccess = useCallback(() => {
        const retryPinsStatusAction = () =>
            new Promise<void>((resolve, reject) => {
                dispatch(
                    getPinsStatus({
                        onSuccess: () => {
                            onOpenInitialPage();
                            dispatch(
                                setSplashScreenState({
                                    visible: false,
                                }),
                            );
                            resolve();
                        },
                        onFailure: reject,
                    }),
                );
            });
        dispatch(
            getPinsStatus({
                onSuccess: onOpenInitialPage,
                onFailure: (error) => {
                    if (isErrorNeedsRetry(error)) {
                        RetryController.showVitalDialog({
                            actionButtonTitle: LANGUAGE_KEYS.RELOAD_BUTTON,
                            actionButtonOnPress: retryPinsStatusAction,
                        });
                        RetryController.registerRetryCallback(
                            retryPinsStatusAction,
                            {
                                retryDuration: 10 * 60 * 1000,
                                maxRetryCount: 10,
                            },
                        );
                        RetryController.executeRetry();
                    }
                },
            }),
        );
    }, [dispatch, onOpenInitialPage]);

    const handleSilentLoginRecoverableError = useCallback(async () => {
        const baseOS = await getBaseOs();
        const isNativeOS = baseOS === 'Android' || baseOS === 'iOS';
        const pageShown = await RegularStorage.getItem(
            StorageKeys.LANDING_PAGE_SHOWN,
        );

        if (
            Platform.OS === 'web' &&
            (isNativeOS ||
                isMobileMediaQuery ||
                isTabletMediaQuery ||
                isWebMinWidthMediaQuery)
        ) {
            navigation?.reset({
                routes: [{ name: SCREEN.LANDING_SCREEN }],
            });
        } else if (!pageShown) {
            navigation?.reset({
                routes: [{ name: SCREEN.WELCOME_SCREEN }],
            });
        } else {
            navigation?.reset({
                routes: [{ name: SCREEN.LOGIN_SCREEN }],
            });
        }

        dispatch(
            setSplashScreenState({
                visible: false,
            }),
        );
    }, [dispatch, navigation]);

    const handleSilentLoginFailure = useCallback(
        async (error: AxiosError | string) => {
            if (isErrorNeedsRetry(error)) {
                const retrySilentLoginAction = () =>
                    new Promise<void>((resolve, reject) => {
                        dispatch(
                            loginSilently({
                                onSuccess: () => {
                                    handleSilentLoginSuccess();
                                    resolve();
                                },
                                onFailure: (_error) => {
                                    if (isErrorNeedsRetry(_error)) {
                                        reject(_error);
                                    } else {
                                        handleSilentLoginRecoverableError();
                                        resolve();
                                    }
                                },
                            }),
                        );
                    });

                RetryController.showVitalDialog({
                    actionButtonTitle: LANGUAGE_KEYS.RELOAD_BUTTON,
                    actionButtonOnPress: retrySilentLoginAction,
                });
                RetryController.registerRetryCallback(retrySilentLoginAction, {
                    maxRetryCount: 10,
                    retryDuration: 10 * 60 * 1000,
                });
                RetryController.executeRetry();
            } else {
                handleSilentLoginRecoverableError();
            }
        },
        [dispatch, handleSilentLoginRecoverableError, handleSilentLoginSuccess],
    );

    const handleSilentLogin = useCallback(() => {
        dispatch(getLanguages({ languages }));
        dispatch(getConsentOrdering());
        dispatch(
            loginSilently({
                onSuccess: handleSilentLoginSuccess,
                onFailure: handleSilentLoginFailure,
            }),
        );
    }, [
        dispatch,
        handleSilentLoginFailure,
        handleSilentLoginSuccess,
        languages,
    ]);

    return { handleSilentLogin };
}

export default useSilentLogin;
